import XmppGroupChatApi from "../api/xmppGroupChatApi"
import {
  iterateThroughArchivePages,
  MAMQueryOptions,
  Order,
  ArchiveFetchResult,
} from "../api/xeps/MAM"
import { Client } from "@xmpp/client"
import { addMilliseconds, isAfter, subDays, subHours } from "date-fns"
import { buildChat, Chat, refreshChats } from "../reducers/chatsSlice"
import {
  selectLastSyncedAt,
  updateLastSyncedAt,
} from "../reducers/chatMetaSlice"
import {
  latestSentOrReceivedMessageInIM,
  selectActiveChat,
} from "../reducers/chatsSliceSelectors"
import { type AppStore, type RootState, STATE_VERSION } from "../reducers/store"
import { defer, redirect, type LoaderFunctionArgs } from "react-router-dom"
import { entryPath } from "../lib/appEntryLink"
import { REHYDRATE } from "redux-persist"
import SettingsApi from "../api/settingsApi"
import { updateSettings } from "../reducers/settingsSlice"
import {
  addToSyncQueue,
  clearSyncedAndFailedRooms,
  setInitSyncRoomCount,
  updatePreRoomSyncInProgress,
  updateSyncMetadata,
} from "../reducers/roomSyncStatusSlice"
import AdHocCommands from "../api/adHocCommands"
import MAMStanzaProcessor from "../api/xeps/MAMStanzaProcessor"
import {
  addBookmark,
  Bookmark,
  retrieveAllBookmarks,
} from "../api/xeps/PepNativeBookmarks"
import {
  attemptToSignInWithCookies,
  clearAuthCookies,
  configureWindowXmpp,
  hasUserAuthCookies,
} from "./SignIn"
import { captureError } from "../ErrorHandlers"
import { acquireTabSlot, MAX_CONCURRENT_TABS } from "../stream/tabSlots"
import * as Sentry from "@sentry/react"
import ChatHelpers from "../lib/chatHelpers"
import { retry } from "../utils"
import { Dispatch } from "@reduxjs/toolkit"
import XmppApi from "../api/xmppApi"
import { setRoster } from "../reducers/rosterSlice"
import {
  markLoadingMetric,
  markMetricDuration,
  resetStart as resetLoadingMetricsStart,
  sendLoadingMetrics,
} from "../loadingMetrics"
import RoomSyncQueue from "../lib/roomSyncQueue"
import { getStoredMyJid } from "../lib/cookieStoredJidStorage"
import {
  ChatMessages,
  DeliveryOrder,
  DocumentDriverContext,
  DocumentLink,
  ExtractedMetadata,
  Fasten,
  GenericMessage,
  limitChatSize,
  Marker,
  Message,
  MessageFileShared,
  MessageRetraction,
  processArchivedBatch,
  Reaction,
} from "../reducers/messagesSlice"
import XmppPubSubApi from "../api/xmppPubSubApi"
import { AccountChat } from "lib/accountChatsParser"
import { setAccountChats } from "../reducers/accountChatsSlice"
import { setUserChats } from "@/reducers/userChatsSlice"
import { waitForRehydration } from "@/lib/waitForRehydration"

const MAX_MESSAGES_IN_CHAT = 100
const RETRY_OPTIONS = { maxAttempts: 5, backoff: true }

/**
 * Runs a step with the standard retry policy.
 * Wraps execution in a Sentry span and emits an analytics event on success,
 * both keyed by `name`. Event properties include `duration` (seconds) and
 * `retries` (0 = first-attempt success).
 */
const performStep = async <T>(
  name: string,
  fn: () => Promise<T>,
): Promise<T> => {
  let attempts = 0
  const start = performance.now()
  const result = await Sentry.startSpan({ name, op: name }, () =>
    retry(fn, [], {
      ...RETRY_OPTIONS,
      onAttempt: () => {
        attempts++
      },
    }),
  )
  window.analytics.track(name, {
    duration: parseFloat(((performance.now() - start) / 1000).toFixed(2)),
    retries: attempts - 1,
  })
  return result
}

const addMillisecond = (iso: string | Date): Date => {
  const date = new Date(iso)
  return addMilliseconds(date, 1)
}

const syncPushNotificationSubscription = (client: Client, store: AppStore) => {
  AdHocCommands.getPushNotificationsSubscription(client)
    .then((sub) =>
      store.dispatch(
        updateSettings({ settings: { pushSubscriptionPresent: !!sub } }),
      ),
    )
    .catch(() =>
      store.dispatch(
        updateSettings({ settings: { pushSubscriptionPresent: false } }),
      ),
    )
}

type ArchiveFetchOptions = {
  queryOptions: MAMQueryOptions
  order: Order
  softMax?: number
}

const A_LOT_OF_ROOMS = 20
export const SOFT_MAX_MESSAGES_COUNT = 10
export const SOFT_MAX_MESSAGES_COUNT_MANY_CHATS = 7

const archiveQueryOptions = (
  state: RootState,
  { chat, chatMessages }: { chat: Chat; chatMessages: ChatMessages },
) => {
  const lastMessage = chatMessages
    ? chatMessages.messages.slice(-1)[0]
    : latestSentOrReceivedMessageInIM(state)

  const chatLastSyncedAt = chat
    ? selectLastSyncedAt(chat.jid)(state)
    : undefined

  let fetchOptions: ArchiveFetchOptions

  if (lastMessage) {
    const afterStamp = getTimestampToFetchFrom(lastMessage, chatLastSyncedAt)

    fetchOptions = {
      queryOptions: {
        afterStamp: afterStamp.toISOString(),
      },
      order: "asc",
    }
  } else {
    fetchOptions = {
      queryOptions: {
        before: true, // fetch last page of the archive
        max: "40",
      },
      order: "desc",
      softMax:
        state.chats.length > A_LOT_OF_ROOMS
          ? SOFT_MAX_MESSAGES_COUNT_MANY_CHATS
          : SOFT_MAX_MESSAGES_COUNT,
    }
  }

  if (chat?.room) {
    fetchOptions.queryOptions.toJid = chat.jid
  }

  return fetchOptions
}

const getTimestampToFetchFrom = (
  lastMessage: GenericMessage,
  chatLastSyncedAt: Date | undefined,
): Date => {
  if (!chatLastSyncedAt) {
    return addMillisecond(getLatestTimestampOfMessage(lastMessage))
  }

  const latestMessageTimestamp = addMillisecond(
    getLatestTimestampOfMessage(lastMessage),
  )

  return isAfter(latestMessageTimestamp, chatLastSyncedAt)
    ? chatLastSyncedAt
    : latestMessageTimestamp
}

const getLatestTimestampOfMessage = (message: GenericMessage): Date => {
  let latestDate = extractTime(message)
  if ("markers" in message) {
    for (const marker of message.markers) {
      const markerDate = extractTime(marker)
      if (isAfter(markerDate, latestDate)) {
        latestDate = markerDate
      }
    }
  }

  return latestDate
}

const extractTime = (message: GenericMessage): Date => {
  if (
    "stanzaId" in message &&
    message.stanzaId &&
    message.stanzaId.match(/^\d{16}$/)
  ) {
    return new Date(Number(message.stanzaId) / 1000)
  }

  if ("id" in message && message.id && message.id.match(/^\d{16}$/)) {
    return new Date(Number(message.id) / 1000)
  }

  return new Date(message.timestamp)
}

export const lastSyncedAtIsFarBehind = (
  lastSyncedAt: Date | undefined,
  state: RootState,
): boolean => {
  if (!lastSyncedAt) return true

  const aWhileAgo =
    state.chats.length > A_LOT_OF_ROOMS
      ? subHours(new Date(), 12)
      : subDays(new Date(), 1)

  return isAfter(aWhileAgo, lastSyncedAt)
}

export const syncSingleRoom = async (
  jid: string,
  client: Client,
  state: RootState,
  dispatch: Dispatch,
): Promise<ArchiveFetchResult> => {
  const chat = state.chats.find((c) => c.room && c.jid === jid)
  if (!chat) {
    console.warn(`Trying to sync single room but no chat found: ${jid}`)
    return { count: 0, hasMore: false }
  }

  let chatMessages = state.messages[chat.jid] || []
  const timestampBeforeSync = new Date().toISOString()

  let isFarBehind = false
  if (lastSyncedAtIsFarBehind(selectLastSyncedAt(chat.jid)(state), state)) {
    isFarBehind = true
    if (chatMessages) {
      chatMessages = { ...chatMessages, messages: [] }
    }
  }

  const fetchOptions = archiveQueryOptions(state, {
    chat,
    chatMessages,
  })
  const messagesAccumulator: Message[][] = []
  const markersAccumulator: Marker[][] = []
  const reactionsAccumulator: Reaction[][] = []
  const messageFileSharesAccumulator: MessageFileShared[][] = []
  const messageRetractionsAccumulator: MessageRetraction[][] = []
  const extractedMetadataAccumulator: ExtractedMetadata[][] = []
  const documentDriverContextAccumulator: DocumentDriverContext[][] = []
  const deliveryOrdersAccumulator: DeliveryOrder[][] = []
  const documentLinksAccumulator: DocumentLink[][] = []
  const fastensAccumulator: Fasten[][] = []

  const stats = await iterateThroughArchivePages(
    client,
    fetchOptions.queryOptions,
    MAMStanzaProcessor.accumulatePages(
      messagesAccumulator,
      markersAccumulator,
      reactionsAccumulator,
      messageFileSharesAccumulator,
      messageRetractionsAccumulator,
      extractedMetadataAccumulator,
      documentDriverContextAccumulator,
      deliveryOrdersAccumulator,
      documentLinksAccumulator,
      fastensAccumulator,
      client,
    ),
    fetchOptions.order,
    fetchOptions.softMax,
  )

  if (chat?.jid) {
    const messageCount = messagesAccumulator.reduce(
      (initial, messages) => initial + messages.length,
      0,
    )
    const markersCount = markersAccumulator.reduce(
      (initial, messages) => initial + messages.length,
      0,
    )
    const reactionsCount = reactionsAccumulator.reduce(
      (initial, messages) => initial + messages.length,
      0,
    )
    const retractionsCount = messageRetractionsAccumulator.reduce(
      (initial, messages) => initial + messages.length,
      0,
    )

    dispatch(
      updateSyncMetadata({
        jid: chat.jid,
        messages: messageCount,
        markers: markersCount,
        reactions: reactionsCount,
        retractions: retractionsCount,
      }),
    )
  }

  // Process the accumulated data and dispatch a single action
  const accumulatedData = {
    messagesAccumulator,
    markersAccumulator,
    reactionsAccumulator,
    messageFileSharesAccumulator,
    messageRetractionsAccumulator,
    extractedMetadataAccumulator,
    documentDriverContextAccumulator,
    deliveryOrdersAccumulator,
    documentLinksAccumulator,
    routeUpdatesAccumulator: fastensAccumulator,
  }

  const payload = MAMStanzaProcessor.processAccumulatedData(
    accumulatedData,
    fetchOptions.order,
    !!fetchOptions.queryOptions.flipPage,
    client.jid!.toString(),
    fetchOptions.order === "asc" ? "append" : "prepend",
  )

  if (isFarBehind) {
    dispatch(limitChatSize({ jid: chat.jid, limit: 0 }))
  }
  dispatch(processArchivedBatch(payload))

  dispatch(
    updateLastSyncedAt({
      jid: chat.jid,
      lastSyncedAt: timestampBeforeSync,
    }),
  )
  dispatch(limitChatSize({ jid: chat.jid, limit: MAX_MESSAGES_IN_CHAT }))

  return stats
}

const createInitialBookmarks = async (client: Client): Promise<Bookmark[]> => {
  const chats = await XmppGroupChatApi.fetchRooms(client)

  const { mandatory, optional } = chats.reduce(
    (res, chat) => {
      if (ChatHelpers.canUserLeaveChat(chat)) {
        res.optional.push(chat)
      } else {
        res.mandatory.push(chat)
      }

      return res
    },
    { mandatory: [], optional: [] } as { mandatory: Chat[]; optional: Chat[] },
  )

  const chatsToBookmark = [...mandatory, ...optional.slice(0, 5)]
  for (const chat of chatsToBookmark) {
    await addBookmark(client, chat.jid, chat.name)
    window.analytics.track("AddBookmark", {
      chatJid: chat?.jid,
      chatName: chat?.name,
      from: "InitialCreateBookmark",
    })
  }

  return chatsToBookmark.map((chat) => ({
    jid: chat.jid,
    autojoin: true,
    name: chat.name,
  }))
}

const fetchBookmarks = async (client: Client): Promise<Bookmark[]> => {
  try {
    const bookmarks = await retrieveAllBookmarks(client)
    if (bookmarks.length === 0) return await createInitialBookmarks(client)
    return bookmarks
  } catch (e: any) {
    if (e.name === "StanzaError" && e.condition === "item-not-found") {
      return await createInitialBookmarks(client)
    } else {
      throw e
    }
  }
}

const fetchAndStoreAccountChats = async (
  client: Client,
  store: AppStore,
): Promise<AccountChat[]> => {
  const accountChats = await XmppPubSubApi.fetchAccountChats(client)
  store.dispatch(setAccountChats(accountChats))
  return accountChats
}

const fetchAndStoreUserChats = async (
  client: Client,
  store: AppStore,
): Promise<AccountChat[]> => {
  const userChats = await XmppPubSubApi.fetchUserChats(client)
  store.dispatch(setUserChats(userChats))
  return userChats
}

export const getBookmarkedRooms = async (client: Client, store: AppStore) => {
  const bookmarks = await fetchBookmarks(client)
  const [accountChatsData, userChatsData] = await Promise.all([
    fetchAndStoreAccountChats(client, store),
    fetchAndStoreUserChats(client, store),
  ])
  const chatsByJid = new Map<string, AccountChat>()
  userChatsData.forEach((chat) => chatsByJid.set(chat.jid, chat))
  accountChatsData.forEach((chat) => chatsByJid.set(chat.jid, chat))

  return bookmarks.map((bookmark) => {
    const chatInfo = chatsByJid.get(bookmark.jid)
    const groups = chatInfo ? chatInfo.groupDescriptions : []
    return buildChat({
      room: true,
      jid: bookmark.jid,
      name: chatInfo?.name || bookmark.name,
      groups,
    })
  })
}

export const performInitialLoad = async (
  client: Client,
  store: AppStore,
): Promise<Types.Api.Chat[]> => {
  return Sentry.startSpan(
    { name: "performInitialLoad", op: "app.init" },
    async () => {
      window.analytics.track("InitialLoadStart")
      store.dispatch(updatePreRoomSyncInProgress(true))
      RoomSyncQueue.clear()
      store.dispatch(clearSyncedAndFailedRooms())
      resetLoadingMetricsStart()

      const roster = await performStep("InitialLoadRosterFetch", () =>
        XmppApi.fetchRoster(client),
      )
      store.dispatch(setRoster({ items: roster }))

      const settings = await performStep("InitialLoadSettingsFetch", () =>
        SettingsApi.getSettings(client),
      )
      if (settings) store.dispatch(updateSettings({ settings }))

      syncPushNotificationSubscription(client, store)

      const chats = await performStep("InitialLoadBookmarksFetch", () =>
        getBookmarkedRooms(client, store),
      )

      store.dispatch(refreshChats({ chats }))
      store.dispatch(setInitSyncRoomCount({ count: chats.length }))

      Sentry.getActiveSpan()?.setAttribute("chats.count", chats.length)

      for (const chat of chats) {
        store.dispatch(addToSyncQueue({ jid: chat.jid }))
      }

      const activeChatJid = selectActiveChat(store.getState())?.jid
      if (activeChatJid) {
        RoomSyncQueue.prioritize(activeChatJid)
      }

      store.dispatch(updatePreRoomSyncInProgress(false))

      markLoadingMetric("chatsLength", chats.length)
      sendLoadingMetrics()

      return store.getState().chats
    },
  )
}

export const loadChatsArchive = (store: AppStore) => {
  return async ({ request }: LoaderFunctionArgs) => {
    const search = new URL(request.url).search
    const signInPath = entryPath("/signin", search)
    // Each tab opens its own XMPP connection, so we only let a limited number
    // of tabs connect at the same time.
    if (!(await acquireTabSlot())) {
      window.analytics.track("TabLimitReached", {
        maxTabs: MAX_CONCURRENT_TABS,
      })
      throw redirect(entryPath("/too-many-tabs", search))
    }

    // We first check for client on window because the native sign in flow
    // does not use cookies, instead configures window.XMPP on each app open
    if (!window.XMPP) {
      if (!hasUserAuthCookies()) {
        throw redirect(signInPath)
      }

      configureWindowXmpp()
    }

    injectReactNativeStore(store)

    return defer({
      myJid: getMyJid(signInPath),
    })
  }
}

const getMyJid = async (signInPath: string) => {
  return Sentry.startSpan({ name: "getMyJid", op: "app.init" }, async () => {
    if (window.ReactNativeWebView) {
      if (!window.myJID) {
        throw new Error("No window.myJID present in native")
      }
      Sentry.getActiveSpan()?.setAttribute("source", "native")
      return window.myJID.toString()
    }

    const hadStoredJid = !!getStoredMyJid()
    let myStoredJid = hadStoredJid ? getStoredMyJid() : null

    let connectResult: Awaited<ReturnType<typeof connectXMPP>>
    if (!myStoredJid) {
      Sentry.getActiveSpan()?.setAttribute("source", "fresh_connect")
      connectResult = await connectXMPP()
      if (connectResult.kind === "ok") {
        myStoredJid = connectResult.jid.toString()
      }
    } else {
      Sentry.getActiveSpan()?.setAttribute("source", "stored_jid")
      connectResult = await connectXMPP()
    }

    if (connectResult.kind === "not-authorized") {
      clearAuthCookies()
      throw redirect(signInPath)
    }

    if (!myStoredJid) {
      window.analytics.track("GetMyJidFailed", {
        hadStoredJid,
        hadAuthCookies: hasUserAuthCookies(),
        hadXmpp: !!window.XMPP,
        hadXmppJid: !!window.XMPP?.jid,
        visibilityState: document.visibilityState,
        connectFailureReason:
          connectResult.kind === "failed" ? connectResult.reason : undefined,
      })
      throw redirect(signInPath)
    }

    return myStoredJid
  })
}

const connectXMPP = async () => {
  const attempt = async () => {
    const result = await attemptToSignInWithCookies()
    if (result.kind === "failed") {
      throw new Error(
        `Failed attempt to signin with cookies (reason: ${result.reason})`,
      )
    }
    return result
  }

  try {
    return await retry(attempt, [], { ...RETRY_OPTIONS, backoff: true })
  } catch {
    return { kind: "failed" as const, reason: "exhausted" }
  }
}

const injectNativeStoreSlice = (
  store: AppStore,
  newState: Record<string, string>,
  key: string,
) => {
  if (!newState[key]) {
    console.warn(`Cannot revive ${key} because it's missing from new state`)
    return
  }

  const data = JSON.parse(newState[key])
  const payload: Record<string, object> = {}
  payload[key] = data
  store.dispatch({
    type: REHYDRATE,
    key: "root",
    payload,
  })
}

const injectReactNativeStore = (store: AppStore) => {
  Sentry.startSpan({ name: "injectReactNativeStore", op: "app.init" }, () => {
    const s = window.performance.now()
    try {
      if (window.injectedStore) {
        try {
          const newState = JSON.parse(window.injectedStore)
          const newStoreVersion = newState._persist
            ? JSON.parse(newState._persist).version
            : STATE_VERSION

          if (newStoreVersion < STATE_VERSION) {
            console.info(
              `Store migration, ${newStoreVersion} => ${STATE_VERSION}`,
            )

            window.analytics.track("PurgeStore", {
              reason: "state_version_changed",
              oldVersion: newStoreVersion,
              newVersion: STATE_VERSION,
            })

            return
          }

          const injectedKeys = [
            "chats",
            "messages",
            "sendoutQueue",
            "chatMeta",
            "profiles",
            "settings",
            "subjects",
            "routes",
            "activities",
            "chatReplies",
            "roster",
            "locations",
            "seenChats",
            "communicatorContacts",
            "accountChats",
          ]
          let successfullyInjectedKeys = 0

          for (const key of injectedKeys) {
            try {
              injectNativeStoreSlice(store, newState, key)
              successfullyInjectedKeys += 1
            } catch {
              console.warn(`Error reviving injectedStore ${key}`)
            }
          }

          Sentry.getActiveSpan()?.setAttributes({
            successfullyInjectedKeys,
            totalKeys: injectedKeys.length,
          })

          window.analytics.track("InjectNative", {
            success: true,
            successfullyInjectedKeys,
            totalKeys: injectedKeys.length,
          })
        } catch (error) {
          console.warn("Error reviving injectedStore")
          console.error(error)
          window.analytics.track("InjectNative", { success: false })
        }
      }
    } finally {
      markMetricDuration("injectNativeStore", s)
      window.injectedStore = ""
    }
  })
}

export const setupPerformInitialLoadOnOnline = (
  xmpp: Client,
  store: AppStore,
): void => {
  xmpp.on("online", async () => {
    const archiveLoadStart = performance.now()
    window.analytics.track("ArchiveLoad.Start")
    if (window.persistor && !window.persistor.getState().bootstrapped) {
      await waitForRehydration(window.persistor)
      window.analytics.track("RehydrationWait", {
        duration: Math.round(performance.now() - archiveLoadStart),
      })
    }
    await performInitialLoad(xmpp, store)
    window.analytics.track("ArchiveLoad.Finish", {
      duration: parseFloat(
        ((performance.now() - archiveLoadStart) / 1000).toFixed(2),
      ),
    })
  })
}
